import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const EMPTY = { item_name: '', category: 'seed', quantity: '', unit: '', unit_price: '', supplier: '', purchase_date: '', expiry_date: '', crop_id: '', notes: '' };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };
const catColor = { seed: '#27ae60', fertilizer: '#3498db', pesticide: '#e74c3c', equipment: '#9b59b6', other: '#95a5a6' };
const catIcon = { seed: '🌱', fertilizer: '🧪', pesticide: '☠️', equipment: '🔧', other: '📦' };

const InventoryTab = () => {
  const [items, setItems] = useState([]);
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchAll = () => {
    Promise.all([
      axios.get(`${API}/agriculture/inventory`, { headers: headers() }),
      axios.get(`${API}/agriculture/crops`, { headers: headers() })
    ]).then(([inv, cr]) => { setItems(inv.data.data); setCrops(cr.data.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (item) => {
    setForm({ ...item, purchase_date: item.purchase_date?.split('T')[0] || '', expiry_date: item.expiry_date?.split('T')[0] || '' });
    setEditId(item.id); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.item_name || !form.quantity || !form.unit) return alert('Item name, quantity and unit are required');
    setSaving(true);
    try {
      if (editId) await axios.put(`${API}/agriculture/inventory/${editId}`, form, { headers: headers() });
      else await axios.post(`${API}/agriculture/inventory`, form, { headers: headers() });
      setShowForm(false); fetchAll();
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await axios.delete(`${API}/agriculture/inventory/${id}`, { headers: headers() });
    fetchAll();
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.category === filter);
  const totalValue = items.reduce((s, i) => s + (parseFloat(i.quantity) * parseFloat(i.unit_price || 0)), 0);
  const { paginatedData, currentPage, setCurrentPage, totalPages } = usePagination(filtered, 10);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Inventory</h2>
        <button onClick={openAdd} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Add Item</button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {['seed', 'fertilizer', 'pesticide', 'equipment', 'other'].map(cat => (
          <div key={cat} style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${catColor[cat]}`, textAlign: 'center' }}>
            <div style={{ fontSize: '24px' }}>{catIcon[cat]}</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: catColor[cat] }}>{items.filter(i => i.category === cat).length}</div>
            <div style={{ fontSize: '12px', color: '#7f8c8d', textTransform: 'capitalize' }}>{cat}</div>
          </div>
        ))}
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #f39c12', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#f39c12' }}>Rs {totalValue.toLocaleString()}</div>
          <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Total Value</div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {['all', 'seed', 'fertilizer', 'pesticide', 'equipment', 'other'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: filter === f ? '#27ae60' : '#f0f0f0', color: filter === f ? 'white' : '#555'
          }}>{catIcon[f] || '📋'} {f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Item', 'Category', 'Quantity', 'Unit Price', 'Total Value', 'Supplier', 'Expiry', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No items found.</td></tr>
            )  : paginatedData.map((item, i) => (
              <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '14px 16px', fontWeight: '600', color: '#2c3e50' }}>{item.item_name}</td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: `${catColor[item.category]}20`, color: catColor[item.category] }}>
                    {catIcon[item.category]} {item.category}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{item.quantity} {item.unit}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>Rs {Number(item.unit_price || 0).toLocaleString()}</td>
                <td style={{ padding: '14px 16px', fontWeight: '600', color: '#27ae60' }}>Rs {(parseFloat(item.quantity) * parseFloat(item.unit_price || 0)).toLocaleString()}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{item.supplier || '—'}</td>
                <td style={{ padding: '14px 16px', color: item.expiry_date && new Date(item.expiry_date) < new Date() ? '#e74c3c' : '#555' }}>
                  {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-PK') : '—'}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => openEdit(item)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                    <button onClick={() => handleDelete(item.id)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
  currentPage={currentPage}
  totalPages={totalPages}
  onPageChange={setCurrentPage}
  totalRecords={filtered.length}
  rowsPerPage={10}
/>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>{editId ? 'Edit Item' : 'Add Inventory Item'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Item Name *</label>
                <input style={inputStyle} value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} placeholder="e.g. Urea Fertilizer" />
              </div>
              <div>
                <label style={labelStyle}>Category</label>
                <select style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="seed">🌱 Seed</option>
                  <option value="fertilizer">🧪 Fertilizer</option>
                  <option value="pesticide">☠️ Pesticide</option>
                  <option value="equipment">🔧 Equipment</option>
                  <option value="other">📦 Other</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Linked Crop</label>
                <select style={inputStyle} value={form.crop_id} onChange={e => setForm({ ...form, crop_id: e.target.value })}>
                  <option value="">-- None --</option>
                  {crops.map(c => <option key={c.id} value={c.id}>{c.crop_name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Quantity *</label>
                <input style={inputStyle} type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>Unit *</label>
                <select style={inputStyle} value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
  <option value="">-- Select Unit --</option>
  <option value="kg">kg</option>
  <option value="gram">gram</option>
  <option value="litre">litre</option>
  <option value="ml">ml</option>
  <option value="bag">bag</option>
  <option value="box">box</option>
  <option value="bottle">bottle</option>
  <option value="piece">piece</option>
  <option value="bundle">bundle</option>
  <option value="ton">ton</option>
</select>
              </div>
              <div>
                <label style={labelStyle}>Unit Price (Rs)</label>
                <input style={inputStyle} type="number" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>Supplier</label>
                <input style={inputStyle} value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} placeholder="Supplier name" />
              </div>
              <div>
                <label style={labelStyle}>Purchase Date</label>
                <input style={inputStyle} type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} />
              </div>
             
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : editId ? 'Update Item' : 'Add Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTab;